import { filter, map } from 'lodash';
import moment from 'moment';

import {
  EnumProcessorSchedulingStatus,
  FilterFindManyProcessorSchedulingInput,
  User,
  Vendor,
} from 'lib/graphql';

export const shownStatuses = [
  EnumProcessorSchedulingStatus.Scheduled,
  EnumProcessorSchedulingStatus.DroppedOff,
  EnumProcessorSchedulingStatus.Killed,
  EnumProcessorSchedulingStatus.Aging,
  EnumProcessorSchedulingStatus.Curing,
  EnumProcessorSchedulingStatus.Invoicing,
  EnumProcessorSchedulingStatus.ReadyPayAtPickup,
  EnumProcessorSchedulingStatus.InvoicePaid,
];

export const completedStatuses = [
  EnumProcessorSchedulingStatus.CancelledByProcessor,
  EnumProcessorSchedulingStatus.CancelledByProducer,
  EnumProcessorSchedulingStatus.Completed,
  EnumProcessorSchedulingStatus.DeniedByProcessor,
];

type FilterArgs = {
  vendorId?: Vendor['_id'];
  customerId?: User['_id'];
  /** When true, only match jobs where the user is a contactUser (not requestedBy) */
  buyerOnly?: boolean;
  /** When true, only match jobs where the user is requestedBy (excludes contactUser matches) */
  producerOnly?: boolean;
  /** When provided, the processorScheduling filter will include a dropoffDate range */
  queryDateRange?: { start: moment.Moment; end: moment.Moment };
};

export function getProcessorSchedulingQueryFilters(
  filters: FilterArgs,
): Record<string, FilterFindManyProcessorSchedulingInput> {
  const f = {
    ...(filters.vendorId ? { vendor: filters.vendorId } : {}),
    ...(filters.customerId
      ? filters.buyerOnly
        ? {
            animalHeads: [
              { cutsheetInformation: [{ contactUser: filters.customerId }] },
            ],
          }
        : filters.producerOnly
          ? { requestedBy: filters.customerId }
          : {
              OR: [
                { requestedBy: filters.customerId },
                {
                  animalHeads: [
                    {
                      cutsheetInformation: [
                        { contactUser: filters.customerId },
                      ],
                    },
                  ],
                },
              ],
            }
      : {}),
  };

  // Build date-range operators when a queryDateRange is provided.
  // Expands the window to always include today so today's actions are visible.
  const dateOperators = filters.queryDateRange
    ? {
        _operators: {
          dropoffDate: {
            gte: filters.queryDateRange.start.format('YYYY-MM-DD'),
            lte: filters.queryDateRange.end.format('YYYY-MM-DD'),
          },
        },
      }
    : {};

  return {
    pendingApprovals: {
      ...f,
      isDeleted: false,
      status: EnumProcessorSchedulingStatus.Requested,
    },
    missingCutsheets: {
      ...f,
      isDeleted: false,
      requestedBy: filters.customerId, // This is all mostly ignored
      // TODO: I think this can all be safely removed now that we have the dedicated query
      AND: [
        {
          animalHeads: [
            {
              cutsheetInformation: [],
            },
          ],
        },
        {
          animalHeads: [
            {
              cutsheetInformation: [{ cutsheet: null }],
            },
          ],
        },
      ],
    },
    notifications: {
      ...f,
    },
    processorScheduling: {
      ...f,
      isDeleted: false,
      // Server-side date filtering keeps the payload small. Child components
      // (ActiveJobsTable, TodaysActionsSection) still do client-side filtering
      // within the returned set for precise per-row date logic.
      ...dateOperators,
    },
    bookingsInProgress: {
      ...f,
      isDeleted: false,
      AND: [
        {
          OR: map(
            filter(
              shownStatuses,
              (s) =>
                (s as EnumProcessorSchedulingStatus) !==
                EnumProcessorSchedulingStatus.Completed,
            ),
            (s: string) => ({
              status: s as EnumProcessorSchedulingStatus,
            }),
          ),
        },
      ],
    },
    bookingsCompleted: {
      ...f,
      isDeleted: false,
      AND: [
        {
          OR: map(completedStatuses, (s) => ({
            status: s as EnumProcessorSchedulingStatus,
          })),
        },
      ],
    },
  };
}
